// photo.ts — read a picked image file into a downscaled JPEG data-URL, ON-DEVICE only.
//
// A hidden <input type=file accept=image/*> opens the native photo/camera sheet on iOS and the
// file picker on desktop; the bytes never leave the device and are never sent to the model — a
// photo attached in chat is filed straight onto a chore completion (proof-of-done) on-device.
//
// We downscale to 1024px on the long edge and encode at JPEG q=0.82: small enough that photos
// round-trip through the backup envelope without bloating it, large enough to look right on a
// phone. If the browser can't encode (rare), we fall back to the original data-URL rather than
// dropping the photo.

const MAX_EDGE = 1024;
const JPEG_QUALITY = 0.82;

export function readDownscaledPhoto(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read that photo — try another."));
    reader.onload = () => {
      const src = reader.result;
      if (typeof src !== "string") {
        reject(new Error("Could not read that photo — try another."));
        return;
      }
      const img = new Image();
      // A failed decode still yields a usable original — never lose the user's photo.
      img.onerror = () => resolve(src);
      img.onload = () => {
        const scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) { resolve(src); return; }
        ctx.drawImage(img, 0, 0, w, h);
        try { resolve(canvas.toDataURL("image/jpeg", JPEG_QUALITY)); }
        catch { resolve(src); }
      };
      img.src = src;
    };
    reader.readAsDataURL(file);
  });
}
